import React from "react";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderTag from "@/enum/OrderTag";
import ShopDTO from "@/dto/shop/ShopDTO";
import OrderTagWrap from "../OrderTagWrap";
import { Tooltip } from "antd";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";

function buildTmailUpgradeInfo(order: OrderDTO) {
    if (!order.tmallLogisticsUpgradeInfo) return null;

    const {
        deliveryTime,
        collectTime,
        promiseCollectTime,
        promiseSignTime,
        signTime,
        designatedLogisticsCompanies,
    } = order.tmallLogisticsUpgradeInfo;

    return (
        <>
            {deliveryTime ? <p>计划出库时间:{deliveryTime}</p> : null}

            {collectTime ? (
                <p>
                    计划揽收时间:
                    {collectTime}
                </p>
            ) : null}
            {promiseCollectTime ? (
                <p>
                    最晚揽收时间:
                    {promiseCollectTime}
                </p>
            ) : null}

            {signTime ? (
                <p>
                    计划派送时间:
                    {signTime}
                </p>
            ) : null}

            {promiseSignTime ? (
                <p>
                    最晚派送时间:
                    {promiseSignTime}
                </p>
            ) : null}

            {designatedLogisticsCompanies?.length ? (
                <p>
                    推荐快递:
                    <span>
                        {designatedLogisticsCompanies
                            .map((logisticsCompany) => LogisticsCompanyNameMap[logisticsCompany])
                            .join()}
                    </span>
                </p>
            ) : null}
        </>
    );
}

/**
 * 淘宝平台标签组件
 * 包含所有淘宝相关的标签逻辑
 */
export default function useTaobaotagIcons(order: OrderDTO, orderTagSet: Set<OrderTag>, shop: ShopDTO) {
    const icons = [];

    if (orderTagSet.has(OrderTag.TMALL_HOME_DELIVERY)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key="TMALL_HOME_DELIVERY"
                orderTag={OrderTag.TMALL_HOME_DELIVERY}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.TB_DECORATION_ORDER)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-unimportant"
                key={OrderTag.TB_DECORATION_ORDER}
                orderTag={OrderTag.TB_DECORATION_ORDER}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.TB_PETTY_FEE_RECEIPT)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-unimportant"
                key={OrderTag.TB_PETTY_FEE_RECEIPT}
                orderTag={OrderTag.TB_PETTY_FEE_RECEIPT}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.TMALL_LOGISTICS_UPGRADE)) {
        icons.push(
            <Tooltip key={OrderTag.TMALL_LOGISTICS_UPGRADE} title={buildTmailUpgradeInfo(order)}>
                <OrderTagWrap className="order-icon icon-important" orderTag={OrderTag.TMALL_LOGISTICS_UPGRADE} />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.TMALL_LOGISTICS_SHIP_AS_REQUIRED)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.TMALL_LOGISTICS_SHIP_AS_REQUIRED}
                orderTag={OrderTag.TMALL_LOGISTICS_SHIP_AS_REQUIRED}
            />,
        );
    }
    if (orderTagSet.has(OrderTag.TMALL_LOGISTICS_SHIP_BY_SF)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.TMALL_LOGISTICS_SHIP_BY_SF}
                orderTag={OrderTag.TMALL_LOGISTICS_SHIP_BY_SF}
            />,
        );
    }
    if (orderTagSet.has(OrderTag.TMALL_LOGISTICS_PROMISE_SHIPPING)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.TMALL_LOGISTICS_PROMISE_SHIPPING}
                orderTag={OrderTag.TMALL_LOGISTICS_PROMISE_SHIPPING}
            />,
        );
    }
    if (orderTagSet.has(OrderTag.TMALL_LOGISTICS_PROMISE_SERVED)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-important"
                key={OrderTag.TMALL_LOGISTICS_PROMISE_SERVED}
                orderTag={OrderTag.TMALL_LOGISTICS_PROMISE_SERVED}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.TAOTIAN_CONSOLIDATED_SHIPPING)) {
        icons.push(
            <OrderTagWrap
                className="order-icon icon-unimportant"
                key={OrderTag.TAOTIAN_CONSOLIDATED_SHIPPING}
                orderTag={OrderTag.TAOTIAN_CONSOLIDATED_SHIPPING}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.TAOTIAN_PAID_FORBIN_CONSIGN)) {
        icons.push(
            <Tooltip
                key={OrderTag.TAOTIAN_PAID_FORBIN_CONSIGN}
                title={<div>该订单暂时还不可以打印发货，请等待订单满足平台的发货条件后再操作打印发货！</div>}
            >
                <OrderTagWrap className="order-icon icon-important" orderTag={OrderTag.TAOTIAN_PAID_FORBIN_CONSIGN} />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.TAOTIAN_SHIP_BY_SF)) {
        icons.push(
            <Tooltip
                key={OrderTag.TAOTIAN_SHIP_BY_SF}
                title="请按平台要求使用顺丰打印发货，否则存在电子面单无法打印以及赔付的风险"
            >
                <OrderTagWrap className="order-icon icon-important" orderTag={OrderTag.TAOTIAN_SHIP_BY_SF} />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.TAOTIAN_XINJIANG_CONSOLIDATE)) {
        icons.push(
            <OrderTagWrap
                key={OrderTag.TAOTIAN_XINJIANG_CONSOLIDATE}
                className="order-icon icon-unimportant"
                orderTag={OrderTag.TAOTIAN_XINJIANG_CONSOLIDATE}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.TAOTIAN_REISSUE)) {
        icons.push(
            <OrderTagWrap
                key={OrderTag.TAOTIAN_REISSUE}
                className="order-icon icon-important"
                orderTag={OrderTag.TAOTIAN_REISSUE}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.TAOTIAN_GOOFISH)) {
        icons.push(
            <OrderTagWrap
                key={OrderTag.TAOTIAN_GOOFISH}
                className="order-icon icon-unimportant"
                orderTag={OrderTag.TAOTIAN_GOOFISH}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.TAOTIAN_INSTANT_RETAIL_PLATFORM_DELIVERY)) {
        icons.push(
            <OrderTagWrap
                key={OrderTag.TAOTIAN_INSTANT_RETAIL_PLATFORM_DELIVERY}
                className="order-icon icon-important"
                orderTag={OrderTag.TAOTIAN_INSTANT_RETAIL_PLATFORM_DELIVERY}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.TAOTIAN_INSTANT_RETAIL_SELLER_DELIVERY)) {
        icons.push(
            <OrderTagWrap
                key={OrderTag.TAOTIAN_INSTANT_RETAIL_SELLER_DELIVERY}
                className="order-icon icon-important"
                orderTag={OrderTag.TAOTIAN_INSTANT_RETAIL_SELLER_DELIVERY}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.TAOTIAN_SUPER_LINK_ORDER)) {
        icons.push(
            <OrderTagWrap
                key={OrderTag.TAOTIAN_SUPER_LINK_ORDER}
                className="order-icon icon-important"
                orderTag={OrderTag.TAOTIAN_SUPER_LINK_ORDER}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.TAOTIAN_OFFICIAL_IDENTIFICATION)) {
        icons.push(
            <OrderTagWrap
                key={OrderTag.TAOTIAN_OFFICIAL_IDENTIFICATION}
                className="order-icon icon-unimportant"
                orderTag={OrderTag.TAOTIAN_OFFICIAL_IDENTIFICATION}
            />,
        );
    }

    if (orderTagSet.has(OrderTag.TAOTIAN_SHIP_BY_SF_JC)) {
        icons.push(
            <Tooltip key={OrderTag.TAOTIAN_SHIP_BY_SF_JC} title="该订单为加价发顺丰(平台集采)，需使用顺丰快递打印发货">
                <OrderTagWrap className="order-icon icon-important" orderTag={OrderTag.TAOTIAN_SHIP_BY_SF_JC} />
            </Tooltip>,
        );
    }

    if (orderTagSet.has(OrderTag.TAOTIAN_URGENT_DELIVERY)) {
        icons.push(
            <OrderTagWrap
                key={OrderTag.TAOTIAN_URGENT_DELIVERY}
                className="order-icon icon-important"
                orderTag={OrderTag.TAOTIAN_URGENT_DELIVERY}
            />,
        );
    }

    return icons;
}
